import type { ReactNode } from 'react';

type BadgeVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

interface BadgeProps {
    variant?: BadgeVariant;
    text?: ReactNode;
    children?: ReactNode;
    pill?: boolean;
    className?: string;
    textClassName?: string;
    title?: string;
    onClick?: () => void;
}

const Badge = ({ variant = 'primary', text, children, pill = false, className = '', textClassName = '', title, onClick }: BadgeProps) => {
    const content = text || children;

    // Map variants to Tailwind classes
    const variantClasses = {
        primary: 'bg-blue-600 text-white',
        secondary: 'bg-zinc-600 text-white',
        success: 'bg-green-600 text-white',
        danger: 'bg-red-600 text-white',
        warning: 'bg-yellow-500 text-black',
        info: 'bg-cyan-500 text-white',
        light: 'bg-zinc-100 text-zinc-800',
        dark: 'bg-zinc-800 text-white'
    }[variant];

    const badgeClasses = [
        'inline-flex items-center px-2.5 py-0.5 text-xs font-medium',
        variantClasses,
        pill ? 'rounded-full' : 'rounded',
        onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : '',
        className
    ]
        .filter(Boolean)
        .join(' ');

    const textClasses = textClassName ? ` ${textClassName}` : '';

    return (
        <span
            className={badgeClasses}
            role={onClick ? 'button' : undefined}
            title={title}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
        >
            <span className={textClasses}>{content}</span>
        </span>
    );
};

export default Badge;
